import {
  Modal,
  IconButton,
  Button,
  ButtonVariant,
  IconButtonVariant,
  IconButtonSize,
  Show
} from "shared/components";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AsyncSelect } from "../../components/AsyncSelect/AsyncSelect";
import {
  loadPropertyOptions,
  loadUnitOptions
} from "../../../api/landlord/asyncUnitProperties";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import addressApi from "../../../api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { invalidateApplicationQueries } from "shared/utils/invalidate-application-queries";
import { RentAmountData, SetRentAmountOptions } from "./SetRentAmountOptions";
import { PreviewApplicationRentByUnit } from "types/application";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { CriteriaRentType } from "shared/types";
import api from "api/landlord/address";

enum View {
  ChooseUnit = "ChooseUnit",
  SetRentOptions = "SetRentOptions"
}

export const MoveModal = ({
  onClose,
  applicationId
}: {
  onClose: () => void;
  applicationId: string;
}) => {
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [selectedUnitName, setSelectedUnitName] = useState<
    string | undefined
  >();
  const [manualRentsInput, setManualRentsInput] = useState<RentAmountData>();
  const [rentChangeType, setRentChangeType] = useState<
    "use_current" | "use_listed" | "manual"
  >("use_current");

  const [view, setView] = useState<View>(View.ChooseUnit);

  const { data: application } = useApplicationSummary(applicationId);

  const { data: listedApplicationRentByUnit } =
    useQuery<PreviewApplicationRentByUnit>({
      queryKey: [
        "PREVIEW_APPLICATION_RENT_BY_UNIT",
        applicationId,
        selectedUnitId
      ],
      queryFn: () =>
        api.getPreviewApplicationRentByUnit(applicationId, selectedUnitId || 0),
      enabled: Boolean(selectedUnitId && applicationId)
    });

  const handleClose = () => {
    setSelectedPropertyId(undefined);
    setSelectedUnitId(undefined);
    setManualRentsInput(undefined);
    setView(View.ChooseUnit);
    onClose();
    setRentChangeType("use_current");
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (
      rentData: {
        change_type: "use_current" | "use_listed" | "manual";
      } & RentAmountData
    ) =>
      addressApi.updateApplicationUnit(
        applicationId,
        Number(selectedUnitId),
        rentData
      ),
    onSuccess: () => {
      toast.success("Application successfully updated");
      invalidateApplicationQueries(queryClient, applicationId);
      handleClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const handleContinue = () => {
    if (view === View.ChooseUnit) setView(View.SetRentOptions);
  };

  const panelClassName = useMemo(() => {
    if (view === View.ChooseUnit) return "max-w-[600px]";

    const applicationCriteriaRentType =
      application?.verification_template?.criteria_rent_type ||
      CriteriaRentType.Both;
    const listedApplicationCriteriaRentType =
      listedApplicationRentByUnit?.criteria_rent_type || CriteriaRentType.Both;

    if (homeSiteRentEnabled)
      if (listedApplicationCriteriaRentType === CriteriaRentType.Both)
        return "max-w-[877px]";
      else if (applicationCriteriaRentType === CriteriaRentType.Both)
        return "max-w-[720px]";

    return "max-w-[697px]";
  }, [
    view,
    homeSiteRentEnabled,
    application?.verification_template?.criteria_rent_type,
    listedApplicationRentByUnit?.criteria_rent_type
  ]);

  const handleConfirm = () => {
    const rentData = {
      change_type: rentChangeType,
      ...manualRentsInput
    };

    if (homeSiteRentEnabled && listedApplicationRentByUnit) {
      rentData.home_rent_amount =
        listedApplicationRentByUnit.criteria_rent_type !== CriteriaRentType.Site
          ? manualRentsInput?.home_rent_amount
          : listedApplicationRentByUnit.home_rent_amount;
      rentData.site_rent_amount =
        listedApplicationRentByUnit.criteria_rent_type !== CriteriaRentType.Home
          ? manualRentsInput?.site_rent_amount
          : listedApplicationRentByUnit.site_rent_amount;
    }

    mutate(rentData);
  };

  useEffect(() => {
    if (
      application &&
      listedApplicationRentByUnit &&
      application.verification_template?.criteria_rent_type !==
        listedApplicationRentByUnit.criteria_rent_type
    )
      setRentChangeType("use_listed");
  }, [application, listedApplicationRentByUnit]);

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={handleClose}
      panelClassName={cx("w-full", panelClassName)}
    >
      <div className="p-6 border-b border-alpha-100 relative">
        <div className="flex flex-row justify-between">
          <p className="m-0 text-xl font-bold leading-8">
            <Show if={view === View.ChooseUnit}>
              Move application to another {unit}
            </Show>
            <Show if={view === View.SetRentOptions}>
              Set rent amount for application
            </Show>
          </p>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.LG}
            onClick={() => {
              handleClose();
            }}
          />
        </div>

        <Show if={view === View.ChooseUnit}>
          <p className="mb-0 mt-2 text-sm text-content-400 max-w-[calc(100%-40px)]">
            Application will be moved to the selected {unit}. Application will
            use the screening settings from the selected {unit}. Individual
            applications will keep the original set of requested information,
            but any missing reports will be added
          </p>
        </Show>
      </div>

      <Show if={view === View.ChooseUnit}>
        <div className="py-8 px-6 flex items-start gap-4 bg-background-900">
          <div className="w-full">
            <AsyncSelect
              id="property"
              label={Property}
              value={selectedPropertyId || ""}
              searchable={true}
              hideOptional
              // @ts-expect-error
              loadOptions={loadPropertyOptions}
              error={propertyError}
              options={[]}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedPropertyId(Number(e.target.value));
                  setPropertyError("");
                  setSelectedUnitId(undefined);
                }
              }}
              additional={{
                page: 1
              }}
            />
          </div>
          <div className="w-full">
            <AsyncSelect
              id="unit"
              label={Unit}
              key={selectedPropertyId}
              searchable={true}
              hideOptional
              value={selectedUnitId || ""}
              options={[]}
              // @ts-expect-error
              loadOptions={loadUnitOptions}
              error={unitError}
              additional={{ page: 1, propertyId: selectedPropertyId }}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedUnitId(Number(e.target.value));
                  setUnitError("");
                  setSelectedUnitName(e.label);
                }
              }}
            />
          </div>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-alpha-100 gap-2">
          <Button className="w-[103px]" onClick={onClose}>
            Back
          </Button>
          <Button
            className="w-[130px]"
            variant={ButtonVariant.PRIMARY}
            onClick={handleContinue}
            disabled={!selectedUnitId}
          >
            Continue
          </Button>
        </div>
      </Show>
      <Show if={view === View.SetRentOptions && selectedUnitId}>
        <SetRentAmountOptions
          rentChangeType={rentChangeType}
          setRentChangeType={setRentChangeType}
          currentApplicationRents={{
            monthly_rent_amount: application?.monthly_rent_amount,
            rent_amount: application?.rent_amount,
            home_rent_amount: application?.home_rent_amount,
            site_rent_amount: application?.site_rent_amount,
            housing_assistance_voucher_amount:
              application?.housing_assistance_voucher_amount,
            pet_rent_amount: application?.pet_rent_amount
          }}
          unitName={selectedUnitName || ""}
          manualRent={manualRentsInput}
          setManualRent={setManualRentsInput}
          currentCriteriaRentType={
            application?.verification_template?.criteria_rent_type ||
            CriteriaRentType.Both
          }
          listedApplicationRentByUnit={listedApplicationRentByUnit}
        />

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
          <Button
            className="w-[125px]"
            onClick={() => setView(View.ChooseUnit)}
          >
            Back
          </Button>
          <Button
            className="w-[153px]"
            loading={isPending}
            variant={ButtonVariant.PRIMARY}
            disabled={
              rentChangeType === "manual" &&
              (!homeSiteRentEnabled
                ? manualRentsInput?.rent_amount?.cents === undefined
                : (listedApplicationRentByUnit?.criteria_rent_type !==
                    CriteriaRentType.Site &&
                    manualRentsInput?.home_rent_amount?.cents === undefined) ||
                  (listedApplicationRentByUnit?.criteria_rent_type !==
                    CriteriaRentType.Home &&
                    manualRentsInput?.site_rent_amount?.cents === undefined))
            }
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      </Show>
    </Modal>
  );
};
